import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import * as XLSX from 'xlsx';
import useApi from 'hooks/useApi';

// Field types that need an in-app capture step (a photo taken/uploaded through
// the UI, or a license scan written by a verification callback) and simply
// can't be represented by a spreadsheet cell. These are always left out of
// the import — never mapped, never required — same treatment CAMERA/FILE/
// ESIGN/FORM fields get in the transaction importer.
const NON_IMPORTABLE_TYPES = ['image', 'license_verification'];

const IGNORE_VALUE = '__ignore__';

// Same country-calling-code list ResourceDataManager.tsx uses for its manual
// "countryCode" dropdown, kept in sync so the import's value mapping offers
// the identical set of options.
const COUNTRY_CODES = [
  '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '87', '087', '90', '91', '92', '93', '94', '95', '98', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998'
];

type DocType = 'excel' | 'csv';

type RowResult = {
  row: number;
  status: 'success' | 'error';
  message: string;
};

interface Field {
  name: string;
  type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
  required: boolean;
  unique?: boolean;
  dropdownSource?: 'teams' | 'countryCode';
  dropdownOptions?: string[];
}

interface ResourceTypeLike {
  id: string;
  name: string;
  fields: Field[];
  category?: 'user' | 'others';
}

interface ImportResourcesDialogProps {
  open: boolean;
  onClose: () => void;
  resourceType: ResourceTypeLike | null;
  clientUID: string | null;
  onImported?: () => void;
}

const normalizeHeader = (value: string) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const splitMulti = (raw: any): string[] => {
  if (raw === undefined || raw === null || raw === '') return [];
  return String(raw)
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const ImportResourcesDialog: React.FC<ImportResourcesDialogProps> = ({ open, onClose, resourceType, clientUID, onImported }) => {
  const { createResourceData, getTeamByClient, showToast } = useApi();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Step 0 — source
  const [docType, setDocType] = useState<DocType>('excel');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Options for any "teams" dropdown field — fetched once per open, same
  // source the manual Add Resource form uses.
  const [teams, setTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Step 1 — field mapping: field name -> column name ('' = not imported)
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});

  // Step 2 — value mapping: field name -> { rawValue -> mapped value }
  const [valueMappings, setValueMappings] = useState<Record<string, Record<string, any>>>({});

  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Step 3 — import
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<RowResult[]>([]);
  const [expectedCount, setExpectedCount] = useState(0);

  const resetState = () => {
    setDocType('excel');
    setFileName(null);
    setParsedRows([]);
    setColumns([]);
    setParseError(null);
    setFieldMapping({});
    setValueMappings({});
    setActiveStepIndex(0);
    setResults([]);
    setImporting(false);
    setExpectedCount(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    const needsTeams = (resourceType?.fields || []).some((f) => f.type === 'dropdown' && f.dropdownSource === 'teams');
    if (!needsTeams) {
      setTeams([]);
      return;
    }
    if (!clientUID) {
      setTeams([]);
      return;
    }
    let cancelled = false;
    setLoadingTeams(true);
    getTeamByClient(clientUID)
      .then((resp: any) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        setTeams(list);
      })
      .catch(() => {
        if (!cancelled) setTeams([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTeams(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resourceType, clientUID]);

  // A "team" dropdown on a user-category resource type is multi-select in
  // the manual form (a driver can belong to more than one team) — mirror
  // that here so a comma-separated cell maps to multiple teams.
  const isMultiTeam = (field: Field) => field.name === 'team' && field.dropdownSource === 'teams' && resourceType?.category === 'user';

  const importableFields = useMemo(() => (resourceType?.fields || []).filter((f) => !NON_IMPORTABLE_TYPES.includes(f.type)), [resourceType]);
  const skippedFields = useMemo(() => (resourceType?.fields || []).filter((f) => NON_IMPORTABLE_TYPES.includes(f.type)), [resourceType]);

  const dropdownOptionsFor = (field: Field): { value: string; label: string }[] => {
    if (field.dropdownSource === 'teams') {
      return teams.map((t) => ({ value: t.uid || t.id, label: t.name || t.title || 'Unknown Team' }));
    }
    if (field.dropdownSource === 'countryCode') {
      return COUNTRY_CODES.map((c) => ({ value: c, label: c }));
    }
    if (Array.isArray(field.dropdownOptions)) {
      return field.dropdownOptions.map((o) => ({ value: o, label: o }));
    }
    return [];
  };

  // ---------- Document parsing ----------

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = importableFields.map((f) => f.name);
    const wsData = XLSX.utils.aoa_to_sheet([headers]);
    XLSX.utils.book_append_sheet(wb, wsData, 'Data');

    const guideRows = [['Column', 'Type', 'Required', 'Allowed values / format']].concat(
      importableFields.map((f) => {
        let hint = '';
        if (f.type === 'dropdown') {
          const opts = dropdownOptionsFor(f).map((o) => o.label);
          hint = opts.join(', ');
          if (isMultiTeam(f)) hint += hint ? ' (separate multiple with a comma)' : '';
        } else if (f.type === 'date') {
          hint = 'e.g. 2026-08-24';
        }
        return [f.name, f.type, f.required ? 'Yes' : 'No', hint];
      })
    );
    const wsGuide = XLSX.utils.aoa_to_sheet(guideRows);
    XLSX.utils.book_append_sheet(wb, wsGuide, 'Field Guide');

    XLSX.writeFile(wb, `Import_Template_${(resourceType?.name || 'Resource').replace(/\s+/g, '_')}.${docType === 'csv' ? 'csv' : 'xlsx'}`);
  };

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        const wb = XLSX.read(data, { type: 'binary' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as Record<string, any>[];
        if (rows.length === 0) {
          setParseError('That file has no data rows below the header.');
          setParsedRows([]);
          setColumns([]);
        } else {
          setParsedRows(rows);
          const colSet = new Set<string>();
          rows.forEach((row) => {
            Object.keys(row).forEach((k) => colSet.add(k));
          });
          const cols = Array.from(colSet);
          setColumns(cols);
          setFieldMapping((prev) => {
            const next = { ...prev };
            importableFields.forEach((f) => {
              if (next[f.name]) return;
              const target = normalizeHeader(f.name);
              const match = cols.find((c) => normalizeHeader(c) === target);
              if (match) next[f.name] = match;
            });
            return next;
          });
        }
      } catch (err) {
        setParseError('Could not read that file. Please upload a valid Excel (.xlsx, .xls) or CSV file.');
        setParsedRows([]);
        setColumns([]);
      }
    };
    reader.onerror = () => setParseError('Could not read that file.');
    reader.readAsBinaryString(file);
  };

  // ---------- Field mapping ----------

  const handleAutoMapByName = () => {
    setFieldMapping((prev) => {
      const next = { ...prev };
      importableFields.forEach((f) => {
        if (next[f.name]) return;
        const target = normalizeHeader(f.name);
        const match = columns.find((c) => normalizeHeader(c) === target);
        if (match) next[f.name] = match;
      });
      return next;
    });
  };

  const handleClearMapping = () => setFieldMapping({});

  const unmappedRequiredFields = importableFields.filter((f) => f.required && !fieldMapping[f.name]);

  // ---------- Value mapping ----------

  const distinctValuesForField = (field: Field): string[] => {
    const col = fieldMapping[field.name];
    if (!col) return [];
    const set = new Set<string>();
    parsedRows.forEach((row) => {
      const raw = row[col];
      if (isMultiTeam(field)) {
        splitMulti(raw).forEach((t) => set.add(t));
      } else {
        const s = String(raw ?? '').trim();
        if (s) set.add(s);
      }
    });
    return Array.from(set);
  };

  const fieldsNeedingValueMapping = importableFields.filter(
    (f) => f.type === 'dropdown' && fieldMapping[f.name] && distinctValuesForField(f).length > 0
  );

  // ---------- Stepper plumbing ----------

  const wizardSteps = useMemo(
    () => (fieldsNeedingValueMapping.length > 0 ? ['Source', 'Field Mapping', 'Value Mapping', 'Import'] : ['Source', 'Field Mapping', 'Import']),
    [fieldsNeedingValueMapping.length]
  );
  const wizardStepIndexOf = (label: string) => wizardSteps.indexOf(label);
  const currentStepLabel = wizardSteps[Math.min(activeStepIndex, wizardSteps.length - 1)];

  useEffect(() => {
    if (activeStepIndex > wizardSteps.length - 1) setActiveStepIndex(wizardSteps.length - 1);
  }, [wizardSteps, activeStepIndex]);

  // Seed sensible defaults (exact case-insensitive option match) whenever the
  // set of fields/values needing a mapping changes, without clobbering
  // choices the user already made.
  useEffect(() => {
    if (activeStepIndex !== wizardStepIndexOf('Value Mapping')) return;
    setValueMappings((prev) => {
      const next = { ...prev };
      fieldsNeedingValueMapping.forEach((field) => {
        const existing = { ...(next[field.name] || {}) };
        const values = distinctValuesForField(field);
        const options = dropdownOptionsFor(field);
        values.forEach((raw) => {
          if (existing[raw] !== undefined) return;
          const match = options.find((o) => normalizeHeader(o.label) === normalizeHeader(raw) || normalizeHeader(o.value) === normalizeHeader(raw));
          existing[raw] = match ? match.value : IGNORE_VALUE;
        });
        next[field.name] = existing;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex]);

  const setValueMapping = (fieldName: string, rawValue: string, mapped: any) => {
    setValueMappings((prev) => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), [rawValue]: mapped }
    }));
  };

  // ---------- Value resolution / payload building ----------

  const getMappedValue = (field: Field, row: Record<string, any>): any => {
    const col = fieldMapping[field.name];
    const raw = col ? row[col] : undefined;
    const vm = valueMappings[field.name] || {};

    if (field.type === 'dropdown') {
      if (isMultiTeam(field)) {
        return splitMulti(raw)
          .map((t) => vm[t])
          .filter((v) => v !== undefined && v !== null && v !== IGNORE_VALUE);
      }
      const key = String(raw ?? '').trim();
      if (!key) return '';
      const mapped = vm[key];
      return mapped === IGNORE_VALUE || mapped === undefined ? '' : mapped;
    }
    return raw === undefined || raw === '' ? '' : String(raw).trim();
  };

  const isValueEmpty = (field: Field, value: any): boolean => {
    if (isMultiTeam(field)) return !Array.isArray(value) || value.length === 0;
    return value === undefined || value === null || value === '';
  };

  // ---------- Import ----------

  const importOneRow = async (row: Record<string, any>): Promise<RowResult> => {
    if (!resourceType) return { row: 0, status: 'error', message: 'No resource type selected' };

    const missing = importableFields
      .filter((f) => f.required && isValueEmpty(f, getMappedValue(f, row)))
      .map((f) => f.name);
    if (missing.length > 0) {
      return { row: 0, status: 'error', message: `Missing required value(s): ${missing.join(', ')}` };
    }

    // Every field on the resource type is included in the payload, same as
    // the manual form does (it always sends the full formData object) —
    // non-importable fields (image/license_verification) are simply left
    // out, same as they're left blank when creating one resource by hand.
    const submitData: Record<string, any> = {};
    importableFields.forEach((f) => {
      submitData[f.name] = getMappedValue(f, row);
    });

    try {
      let resp: any;
      if (resourceType.category === 'user') {
        const additionalPayload = { category: 'user', ...submitData };
        resp = await createResourceData(resourceType.id, submitData, additionalPayload);
      } else {
        resp = await createResourceData(resourceType.id, submitData);
      }
      if (resp && resp.success === false) {
        return { row: 0, status: 'error', message: resp.message || 'Failed to create resource' };
      }
      return { row: 0, status: 'success', message: 'Created' };
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to create resource';
      return { row: 0, status: 'error', message };
    }
  };

  const runImport = async () => {
    if (!resourceType) return;
    const rows = parsedRows;
    setExpectedCount(rows.length);
    setImporting(true);
    setResults([]);

    const collected: RowResult[] = [];
    for (let i = 0; i < rows.length; i++) {
      const result = await importOneRow(rows[i]);
      collected.push({ ...result, row: i + 1 });
      setResults([...collected]);
    }

    setImporting(false);
    const successCount = collected.filter((r) => r.status === 'success').length;
    if (successCount > 0) {
      showToast(`Imported ${successCount} of ${rows.length} resource${rows.length === 1 ? '' : 's'}`, successCount === rows.length ? 'success' : 'warning');
      onImported?.();
    } else {
      showToast('No resources were created. See the details below.', 'error');
    }
  };

  const canGoNext = (() => {
    if (currentStepLabel === 'Source') return parsedRows.length > 0 && !parseError;
    if (currentStepLabel === 'Field Mapping') return unmappedRequiredFields.length === 0;
    return true;
  })();

  const handleNext = () => setActiveStepIndex((i) => Math.min(i + 1, wizardSteps.length - 1));
  const handleBack = () => setActiveStepIndex((i) => Math.max(i - 1, 0));

  const successCount = results.filter((r) => r.status === 'success').length;
  const failureCount = results.filter((r) => r.status === 'error').length;
  const canImport = !!resourceType && parsedRows.length > 0 && !importing;

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Import Resources
        <IconButton size="small" onClick={onClose} disabled={importing}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!resourceType ? (
          <Alert severity="warning">Select a resource type above before importing.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create <strong>{resourceType.name}</strong> records from a document instead of filling the form by hand, one row per record.
            </Typography>

            <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ mb: 3 }}>
              {wizardSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* ---- Source ---- */}
            {currentStepLabel === 'Source' && (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="doc-type-label">Document Type</InputLabel>
                    <Select labelId="doc-type-label" label="Document Type" value={docType} onChange={(e) => setDocType(e.target.value as DocType)}>
                      <MenuItem value="excel">Excel (.xlsx, .xls)</MenuItem>
                      <MenuItem value="csv">CSV (.csv)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Every data row in the file will be imported, each creating its own {resourceType.name} record — upload as many rows as you need.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
                    Download Template
                  </Button>
                  <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={handleChooseFile}>
                    Choose File
                  </Button>
                  <input ref={fileInputRef} type="file" accept={docType === 'csv' ? '.csv' : '.xlsx,.xls,.csv'} hidden onChange={handleFileChange} />
                  {fileName && <Chip size="small" icon={<InsertDriveFileIcon />} label={fileName} />}
                </Box>

                {parseError && <Alert severity="error">{parseError}</Alert>}

                {parsedRows.length > 0 && !parseError && (
                  <Alert severity="success">
                    {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'} detected, {columns.length} column{columns.length === 1 ? '' : 's'} found.
                  </Alert>
                )}

                {skippedFields.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {skippedFields.map((f) => f.name).join(', ')} can't be filled from a document — a photo or license scan needs to be captured
                    in the app — so {skippedFields.length === 1 ? 'it' : 'they'} will be left blank for you to complete after import.
                  </Alert>
                )}

                {loadingTeams && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Loading teams for the "team" field…
                  </Alert>
                )}
              </Box>
            )}

            {/* ---- Field Mapping ---- */}
            {currentStepLabel === 'Field Mapping' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Choose which column in your file fills each field.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<AutoFixHighIcon />} onClick={handleAutoMapByName}>
                      Auto-map by name
                    </Button>
                    <Button size="small" onClick={handleClearMapping}>
                      Clear
                    </Button>
                  </Box>
                </Box>

                {unmappedRequiredFields.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Required: {unmappedRequiredFields.map((f) => f.name).join(', ')}
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Column in file</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importableFields.map((field) => (
                        <TableRow key={field.name}>
                          <TableCell>
                            {field.name}
                            {field.required && (
                              <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                                *
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={field.type} />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth>
                              <Select
                                displayEmpty
                                value={fieldMapping[field.name] || ''}
                                onChange={(e) => setFieldMapping((prev) => ({ ...prev, [field.name]: e.target.value as string }))}
                              >
                                <MenuItem value="">
                                  <em>Do not import</em>
                                </MenuItem>
                                {columns.map((c) => (
                                  <MenuItem key={c} value={c}>
                                    {c}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* ---- Value Mapping ---- */}
            {currentStepLabel === 'Value Mapping' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Match the values found in your file to the values this field expects.
                </Typography>

                {fieldsNeedingValueMapping.map((field) => {
                  const values = distinctValuesForField(field);
                  const options = dropdownOptionsFor(field);
                  return (
                    <Box key={field.name} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {field.name} <Chip size="small" label={field.type} sx={{ ml: 1 }} />
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Value in file</TableCell>
                              <TableCell>Maps to</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {values.map((raw) => (
                              <TableRow key={raw}>
                                <TableCell>{raw}</TableCell>
                                <TableCell>
                                  <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Select
                                      displayEmpty
                                      value={valueMappings[field.name]?.[raw] ?? ''}
                                      onChange={(e) => setValueMapping(field.name, raw, e.target.value)}
                                    >
                                      <MenuItem value={IGNORE_VALUE}>
                                        <em>Ignore this value</em>
                                      </MenuItem>
                                      {options.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* ---- Import ---- */}
            {currentStepLabel === 'Import' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Ready to import {parsedRows.length} {resourceType.name} record{parsedRows.length === 1 ? '' : 's'}.
                </Typography>

                {importing && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={expectedCount ? (results.length / expectedCount) * 100 : 0} />
                    <Typography variant="caption" color="text.secondary">
                      Processing {results.length} of {expectedCount}…
                    </Typography>
                  </Box>
                )}

                {results.length > 0 && (
                  <>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {successCount} created, {failureCount} failed
                    </Typography>
                    <List dense sx={{ maxHeight: 260, overflowY: 'auto' }}>
                      {results.map((r) => (
                        <ListItem key={r.row} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {r.status === 'success' ? <CheckCircleIcon color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText primary={`Row ${r.row}`} secondary={r.message} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={importing}>
          {results.length > 0 ? 'Close' : 'Cancel'}
        </Button>
        {resourceType && currentStepLabel !== 'Source' && (
          <Button onClick={handleBack} disabled={importing}>
            Back
          </Button>
        )}
        {resourceType && currentStepLabel !== 'Import' && (
          <Button variant="contained" onClick={handleNext} disabled={!canGoNext}>
            Next
          </Button>
        )}
        {currentStepLabel === 'Import' && (
          <Button variant="contained" onClick={runImport} disabled={!canImport || results.length > 0}>
            {importing ? <CircularProgress size={20} color="inherit" /> : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportResourcesDialog;
